import { object, string, custom } from 'zod';

import { makeZodValidate as makeValidate } from '@occc-app/design-system';
import { IS_REQUIRED, isStringLengthUpTo } from '@occc-app/lib/constants/validationMessages';

import {
  MAX_ASSET_NAME_LENGTH,
  MAX_ASSET_DESCRIPTION_LENGTH,
  MAX_FILE_SIZE_MB,
} from '../../touchpoint.constants';

export const validateImageFile = (file: File): string | null => {
  const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
  if (!validTypes.includes(file.type)) {
    return 'File must be PNG or JPEG';
  }
  const maxSizeBytes = MAX_FILE_SIZE_MB * 1024 * 1024;
  if (file.size > maxSizeBytes) {
    return `File size must be less than ${MAX_FILE_SIZE_MB}MB`;
  }
  return null;
};

const fileSchema = custom<File | null>((file) => {
  if (!file) {
    return true; // Optional files are allowed
  }
  if (!(file instanceof File)) {
    return false;
  }
  const validationError = validateImageFile(file);
  return !validationError;
}, 'Invalid file');

const schema = object({
  name: string({ required_error: IS_REQUIRED })
    .trim()
    .min(1, IS_REQUIRED)
    .max(MAX_ASSET_NAME_LENGTH, isStringLengthUpTo(MAX_ASSET_NAME_LENGTH)),
  description: string({ required_error: IS_REQUIRED })
    .trim()
    .min(1, IS_REQUIRED)
    .max(MAX_ASSET_DESCRIPTION_LENGTH, isStringLengthUpTo(MAX_ASSET_DESCRIPTION_LENGTH)),
  squareImage: fileSchema.optional(),
  squareImageUrl: string({ required_error: IS_REQUIRED })
    .nullable()
    .refine((val) => val !== null && val !== undefined && val.trim().length > 0, {
      message: IS_REQUIRED,
    }),
  landscapeImage: fileSchema.optional(),
  portraitImage: fileSchema.optional(),
  landscapeImageUrl: string({ required_error: IS_REQUIRED })
    .nullable()
    .refine((val) => val !== null && val !== undefined && val.trim().length > 0, {
      message: IS_REQUIRED,
    }),
  portraitImageUrl: string({ required_error: IS_REQUIRED })
    .nullable()
    .refine((val) => val !== null && val !== undefined && val.trim().length > 0, {
      message: IS_REQUIRED,
    }),
});

export const validate = makeValidate(schema);
